/**
 * LevelingService.js
 * Unified XP and leveling system for ODV Platform
 * Uses exponential progression: 6% increase per level
 *
 * This service provides consistent XP calculations across all platform components
 * matching the established ODV progression system documented in ENHANCED_LEADERBOARD_SYSTEM.md
 */

class LevelingService {
  /**
   * Calculate valet level based on total XP
   * Uses 6% exponential increase per level
   */
  static calculateLevel(totalXP) {
    // Exponential XP requirements - Infinite progression with 6% increase per level
    let level = 1;
    let xpNeeded = 100; // Starting XP for level 2
    let totalXPRequired = 0;

    // Infinite progression - no level cap
    while (totalXPRequired + xpNeeded <= totalXP) {
      totalXPRequired += xpNeeded;
      level++;
      // 6% increase per level for sustainable progression
      xpNeeded = Math.floor(xpNeeded * 1.06);
    }

    // Infinite progression titles - continue beyond LEGEND
    if (level >= 200) return { level, title: '🌟 Ascended Legend', color: '#FFD700', tier: 'ascended' };
    if (level >= 150) return { level, title: '💫 Eternal Legend', color: '#FFD700', tier: 'eternal' };

    // Official ODV Level System - "Valet" is earned after Apprentice
    if (level >= 100) return { level, title: '🏁 LEGEND', color: '#FFD700', tier: 'legend' };
    if (level >= 80) return { level, title: '👑 Grand Master', color: '#FFD700', tier: 'grandMaster' };
    if (level >= 70) return { level, title: '🔥 Master', color: '#FF4500', tier: 'master' };
    if (level >= 60) return { level, title: '🐐 GOAT', color: '#32CD32', tier: 'goat' };
    if (level >= 50) return { level, title: '🔬 Parkologist', color: '#4169E1', tier: 'parkologist' };
    if (level >= 40) return { level, title: '⭐ All-Star', color: '#FFD700', tier: 'allStar' };
    if (level >= 30) return { level, title: '🏆 All-Pro', color: '#FF6347', tier: 'allPro' };
    if (level >= 20) return { level, title: '🚀 Professional', color: '#FF69B4', tier: 'pro' };
    if (level >= 15) return { level, title: '🏅 Skilled', color: '#AEA36D', tier: 'skilled' };
    if (level >= 10) return { level, title: '🎖️ Valet', color: '#9B59B6', tier: 'valet' };
    if (level >= 5) return { level, title: '🎓 Apprentice', color: '#87CEEB', tier: 'apprentice' };
    return { level, title: '🚗 Rookie', color: '#BDC3C7', tier: 'rookie' };
  }

  /**
   * Calculate total XP required for a specific level
   * Uses the 6% exponential progression formula
   */
  static getXPRequiredForLevel(targetLevel) {
    if (targetLevel <= 1) return 0;

    let totalXP = 0;
    let xpNeeded = 100; // XP needed for level 2

    for (let level = 2; level <= targetLevel; level++) {
      totalXP += xpNeeded;
      xpNeeded = Math.floor(xpNeeded * 1.06); // 6% increase each level
    }

    return totalXP;
  }

  /**
   * Get next milestone information
   */
  static getNextMilestone(totalXP) {
    const currentLevel = this.calculateLevel(totalXP);
    const nextLevel = currentLevel.level + 1;

    // Infinite progression - no max level
    const nextLevelXP = this.getXPRequiredForLevel(nextLevel);
    const currentLevelXP = this.getXPRequiredForLevel(currentLevel.level);
    const xpNeededForNext = nextLevelXP - currentLevelXP;
    const xpProgressInLevel = totalXP - currentLevelXP;

    return {
      currentLevel: currentLevel.level,
      nextLevel: nextLevel,
      nextLevelXP: nextLevelXP,
      xpNeeded: nextLevelXP,
      remaining: nextLevelXP - totalXP,
      progress: totalXP / nextLevelXP,
      levelProgress: xpProgressInLevel / xpNeededForNext,
      reward: this.getMilestoneReward(nextLevel),
      estimatedAssignments: Math.ceil((nextLevelXP - totalXP) / 540) // Assuming 540 XP per assignment (300 foundation + 240 components avg)
    };
  }

  /**
   * Get milestone reward information
   */
  static getMilestoneReward(targetLevel) {
    // Special title rewards for milestone levels
    const milestoneRewards = {
      5: { bonus: 50, badge: '🎓 Apprentice', description: 'Learning the ropes of professional service' },
      10: { bonus: 100, badge: '🎖️ Valet', description: 'Earned the prestigious title of Valet' },
      15: { bonus: 150, badge: '🏅 Skilled', description: 'Demonstrating competency in all areas' },
      20: { bonus: 200, badge: '🚀 Professional', description: 'Consistently professional service delivery' },
      30: { bonus: 300, badge: '🏆 All-Pro', description: 'Top-tier performance and reliability' },
      40: { bonus: 400, badge: '⭐ All-Star', description: 'Exceptional service that stands out' },
      50: { bonus: 500, badge: '🔬 Parkologist', description: 'Master of the science of parking' },
      60: { bonus: 600, badge: '🐐 GOAT', description: 'Greatest Of All Time status achieved' },
      70: { bonus: 700, badge: '🔥 Master', description: 'Elite mastery of excellence' },
      80: { bonus: 800, badge: '👑 Grand Master', description: 'Legendary leadership' },
      100: { bonus: 1000, badge: '🏁 LEGEND', description: 'The pinnacle of service achievement' }
    };

    if (milestoneRewards[targetLevel]) {
      return milestoneRewards[targetLevel];
    }

    // Standard level up rewards
    return {
      bonus: Math.floor(targetLevel * 2),
      badge: `Level ${targetLevel}`,
      description: `Advanced to Level ${targetLevel} - dedication recognized!`
    };
  }

  /**
   * Get XP breakdown by level ranges for analytics
   */
  static getXPBreakdown() {
    const breakdown = [];
    const ranges = [
      { min: 1, max: 4, name: 'Rookie' },
      { min: 5, max: 9, name: 'Apprentice' },
      { min: 10, max: 14, name: 'Valet' },
      { min: 15, max: 19, name: 'Skilled' },
      { min: 20, max: 29, name: 'Professional' },
      { min: 30, max: 39, name: 'All-Pro' },
      { min: 40, max: 49, name: 'All-Star' },
      { min: 50, max: 59, name: 'Parkologist' },
      { min: 60, max: 69, name: 'GOAT' },
      { min: 70, max: 79, name: 'Master' },
      { min: 80, max: 99, name: 'Grand Master' },
      { min: 100, max: 100, name: 'LEGEND' }
    ];

    ranges.forEach(range => {
      const minXP = this.getXPRequiredForLevel(range.min);
      const maxXP = this.getXPRequiredForLevel(range.max);
      breakdown.push({
        ...range,
        minXP,
        maxXP,
        xpRange: maxXP - minXP
      });
    });

    return breakdown;
  }

  /**
   * Format XP display with commas
   */
  static formatXP(xp) {
    return xp.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * Calculate years of service estimate based on XP
   * Updated for foundation + components structure
   * Assumes ~175 assignments per year (3-4 shifts/week) at 300 XP foundation + ~240 XP components avg
   */
  static estimateYearsOfService(totalXP) {
    const foundationXPPerYear = 175 * 300; // 52,500 XP foundation per year
    const componentXPPerYear = 175 * 8 * 30; // 42,000 XP components (8 guests avg)
    const totalXPPerYear = foundationXPPerYear + componentXPPerYear; // ~94,500 XP per year
    return (totalXP / totalXPPerYear).toFixed(1);
  }

  /**
   * Get level tier for simplified categorization
   */
  static getLevelTier(level) {
    if (level >= 80) return 'elite';
    if (level >= 50) return 'veteran';
    if (level >= 20) return 'professional';
    if (level >= 10) return 'developing';
    return 'beginner';
  }

  /**
   * Quick reference table for XP requirements
   * Useful for displaying progression paths
   */
  static getProgressionTable() {
    const levels = [1, 5, 10, 15, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    return levels.map(level => {
      const xp = this.getXPRequiredForLevel(level);
      const levelInfo = this.calculateLevel(xp);
      return {
        level,
        totalXP: xp,
        title: levelInfo.title,
        yearsEstimate: this.estimateYearsOfService(xp)
      };
    });
  }
}

export default LevelingService;
